import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { InputSwitch } from 'primereact/inputswitch';
import { useGetCampaignLayerContent, useUpdateCampaignContentSettings, useUpdatePlaylistContentSettings, useUpdateCampaignMutation } from "../../../../../../queries";

/**
 * AreaSettingsToggles
 * Handles global mute/fill toggles for a layout area — extracted from AreaControls.
 */
const AreaSettingsToggles = ({ area, index, isDarkMode, onSettingChange, queryClient }) => {
    const { t } = useTranslation();
    const { data: content } = useGetCampaignLayerContent(area.id, "MAIN");
    const updateCampaignContentMutation = useUpdateCampaignContentSettings();
    const updatePlaylistContentMutation = useUpdatePlaylistContentSettings();
    const { mutate: updateCampaign } = useUpdateCampaignMutation();

    // Refs for stable access inside timeout
    const contentRef = React.useRef(content);
    const optimisticStateRef = React.useRef({});
    const timerRef = React.useRef(null);

    // Local optimistic state for UI rendering
    const [optimisticState, setOptimisticState] = useState({});

    // Keep contentRef updated
    useEffect(() => {
        contentRef.current = content;
    }, [content]);

    // Reset optimistic state when area changes
    useEffect(() => {
        setOptimisticState({});
        optimisticStateRef.current = {};
        if (timerRef.current) clearTimeout(timerRef.current);
    }, [area.id]);

    // Compute states
    const hasContent = content && content.length > 0;
    
    // Check if ALL items match the setting (Derived State)
    const isAllMutedDerived = hasContent && content.every(c => Number(c.is_muted) === 1);
    const isAllFilledDerived = hasContent && content.every(c => !c.maintain_aspect_ratio || Number(c.maintain_aspect_ratio) === 0);

    // Sync Optimistic State with Reality
    useEffect(() => {
        if (!hasContent) return;
        
        setOptimisticState(prev => {
            const newState = { ...prev };
            let hasChanges = false;

            if (newState.is_muted !== undefined) {
               const expected = newState.is_muted === 1;
               if (isAllMutedDerived === expected) {
                   delete newState.is_muted;
                   hasChanges = true;
               }
            }

            if (newState.maintain_aspect_ratio !== undefined) {
                const expected = newState.maintain_aspect_ratio === 0;
                if (isAllFilledDerived === expected) {
                    delete newState.maintain_aspect_ratio;
                    hasChanges = true;
                }
            }

            return hasChanges ? newState : prev;
        });
    }, [content, isAllMutedDerived, isAllFilledDerived, hasContent]);


    // Determine Checked State (UI)
    const mutedChecked = optimisticState.is_muted !== undefined 
        ? optimisticState.is_muted === 1 
        : (hasContent ? isAllMutedDerived : !!area.settings?.live?.is_muted);

    const filledChecked = optimisticState.maintain_aspect_ratio !== undefined
        ? optimisticState.maintain_aspect_ratio === 0
        : (hasContent ? isAllFilledDerived : !area.settings?.live?.maintain_aspect_ratio);

    // AUTO-SYNC: Fix Mismatch between Content State and Layout Setting on Load
    useEffect(() => {
        if (!hasContent) return;
        if (optimisticState.is_muted !== undefined || optimisticState.maintain_aspect_ratio !== undefined) return;
        
        const layoutMuted = !!area.settings?.live?.is_muted;
        const contentMuted = isAllMutedDerived;

        if (layoutMuted !== contentMuted) {
             syncLayoutSettingsOnly('is_muted', contentMuted ? 1 : 0);
        }

        const layoutMaintain = !!area.settings?.live?.maintain_aspect_ratio;
        const contentMaintain = !isAllFilledDerived; 
        
        if (layoutMaintain !== contentMaintain) {
             syncLayoutSettingsOnly('maintain_aspect_ratio', contentMaintain ? 1 : 0);
        }

    }, [hasContent, isAllMutedDerived, isAllFilledDerived, area.settings?.live?.is_muted, area.settings?.live?.maintain_aspect_ratio]);


    const executeGlobalUpdate = async () => {
        const currentContent = contentRef.current;
        if (!currentContent || currentContent.length === 0) return;

        const overrides = optimisticStateRef.current;
        const isPlaylist = window.location.pathname.includes("playlist");
        const updateMutation = isPlaylist ? updatePlaylistContentMutation : updateCampaignContentMutation;

        const itemsToUpdate = currentContent.filter(item => {
            let needsUpdate = false;
            if (overrides.is_muted !== undefined && item.is_muted !== overrides.is_muted) needsUpdate = true;
            if (overrides.maintain_aspect_ratio !== undefined && item.maintain_aspect_ratio !== overrides.maintain_aspect_ratio) needsUpdate = true;
            return needsUpdate;
        });
        
        if (itemsToUpdate.length === 0) return;

        await Promise.all(itemsToUpdate.map(async (item) => {
            const formData = new FormData();
            
            const finalMuted = overrides.is_muted !== undefined ? overrides.is_muted : item.is_muted;
            const finalAspectRatio = overrides.maintain_aspect_ratio !== undefined ? overrides.maintain_aspect_ratio : item.maintain_aspect_ratio;

            const settings = {
                "show_video_screen": item.show_video_screen,
                "is_muted": finalMuted,
                "maintain_aspect_ratio": finalAspectRatio,
                "settings": item.media_settings,
                "is_showed": item.is_showed,
                "downloadable": item.downloadable,
                "show_layer_two": item.show_layer_two,
                "show_layer_one": item.show_layer_one,
                "show_layer_three": item.show_layer_three,
            };

            for (let k in settings) {
                if (k === "settings") {
                     const orderedSettings = {
                        width: item.media_settings?.width || 100,
                        height: item.media_settings?.height || 100,
                        top_margin: item.media_settings?.top_margin || 0,
                        start_margin: item.media_settings?.start_margin || 0,
                    };
                    formData.append("settings", JSON.stringify(orderedSettings));
                } else {
                    formData.append(k, Number(settings[k]));
                }
            }
            
            if (isPlaylist) {
                formData.append("playlist_media_id", item.playlist_media_id);
            }

            try {
                await updateMutation.mutateAsync({
                    data: formData,
                    id: isPlaylist ? undefined : item.campaign_playlist_media_id,
                    suppressDialog: true
                });
            } catch (error) {
                console.error("Failed to update content item:", item.id, error);
            }
        }));

        // Also update the Screen Settings (Layout Level)
        if (!isPlaylist && (overrides.is_muted !== undefined || overrides.maintain_aspect_ratio !== undefined)) {
            const currentSettings = area.settings?.live || {};
            
            const newSettings = {
                ...currentSettings,
            };

            if (overrides.is_muted !== undefined) newSettings.is_muted = overrides.is_muted;
            if (overrides.maintain_aspect_ratio !== undefined) newSettings.maintain_aspect_ratio = overrides.maintain_aspect_ratio;

            try {
                updateCampaign({
                    id: area.id,
                    data: {
                        "status": "DRAFT",
                        "name": area.name,
                        "settings": { live: newSettings }
                    },
                    suppressDialog: true
                });
            } catch (error) {
                console.error("Failed to update screen settings:", area.id, error);
            }
        }

        // Invalidate query cache
        if (queryClient) {
            queryClient.invalidateQueries(['getCampaignLayerContent', area.id, 'MAIN']);
            
            if (currentContent) {
                const playlistIds = currentContent
                    .filter(item => item.type === "EVENT" && item.playlist_id)
                    .map(item => item.playlist_id);
                
                playlistIds.forEach(playlistId => {
                    queryClient.invalidateQueries(['getPlaylistLayerContent', playlistId, 'MAIN']);
                });
            }
        }
    };

    const syncLayoutSettingsOnly = (key, value) => {
        onSettingChange(index, key, value);

        const currentSettings = area.settings?.live || {};
        const newSettings = {
            ...currentSettings,
            [key]: value
        };

        try {
            updateCampaign({
                id: area.id,
                data: {
                    "status": "DRAFT",
                    "name": area.name,
                    "settings": { live: newSettings }
                },
                suppressDialog: true
            });
        } catch (error) {
            console.error("Failed to sync layout settings:", area.id, error);
        }
    };

    const handleUpdate = (key, value) => {
        setOptimisticState(prev => ({ ...prev, [key]: value }));
        optimisticStateRef.current = { ...optimisticStateRef.current, [key]: value };

        onSettingChange(index, key, value);

        if (hasContent) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(executeGlobalUpdate, 500);
        }
    }

    return (
        <div className="flex items-center justify-between mb-3 px-1 gap-2">
            <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border w-full justify-between transition-colors ${
                isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-100'
            }`}>
                <label className={`text-[10px] font-bold uppercase tracking-wider cursor-pointer ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>{t('layouts.designer_mute_all')}</label>
                <InputSwitch 
                    checked={mutedChecked} 
                    onChange={(e) => handleUpdate('is_muted', e.value ? 1 : 0)}
                    className="scale-50 origin-right"
                    pt={{
                        slider: ({ props }) => ({
                            className: props.checked ? 'bg-indigo-500' : (isDarkMode ? 'bg-slate-700' : 'bg-gray-300')
                        })
                    }}
                />
            </div>
            <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border w-full justify-between transition-colors ${
                isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gray-50 border-gray-100'
            }`}>
                <label className={`text-[10px] font-bold uppercase tracking-wider cursor-pointer ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}>{t('layouts.designer_fill_all')}</label>
                <InputSwitch 
                    checked={filledChecked} 
                    onChange={(e) => handleUpdate('maintain_aspect_ratio', e.value ? 0 : 1)}
                    className="scale-50 origin-right"
                    pt={{
                        slider: ({ props }) => ({
                            className: props.checked ? 'bg-indigo-500' : (isDarkMode ? 'bg-slate-700' : 'bg-gray-300')
                        })
                    }}
                />
            </div>
        </div>
    );
};

export default AreaSettingsToggles;
